/**
 * Navigation Provider
 * Provides quick navigation links to main pages
 */

import type { ReactNode } from 'react';
import { LayoutDashboard, Users, FileText, Receipt, PlusCircle, Settings } from 'lucide-react';
import type { SearchProvider, SearchResult, SearchAction } from '../types';

interface NavItem {
  id: string;
  title: string;
  description: string;
  path: string;
  icon: ReactNode;
  keywords: string[];
  shortcut?: string;
}

const NAVIGATION_ITEMS: NavItem[] = [
  {
    id: 'nav-dashboard',
    title: 'Dashboard',
    description: 'Ir al panel principal',
    path: '/dashboard',
    icon: <LayoutDashboard className="h-4 w-4 text-indigo-500" />,
    keywords: ['dashboard', 'inicio', 'home', 'panel', 'principal'],
    shortcut: '⌘D',
  },
  {
    id: 'nav-doctors',
    title: 'Lista de Médicos',
    description: 'Ver todos los médicos',
    path: '/doctors',
    icon: <Users className="h-4 w-4 text-blue-500" />,
    keywords: ['medicos', 'doctores', 'lista', 'doctors'],
    shortcut: '⌘M',
  },
  {
    id: 'nav-doctors-new',
    title: 'Nuevo Médico',
    description: 'Registrar un nuevo médico',
    path: '/doctors/new',
    icon: <PlusCircle className="h-4 w-4 text-green-500" />,
    keywords: ['nuevo', 'medico', 'crear', 'agregar', 'registrar', 'doctor'],
  },
  {
    id: 'nav-tasks',
    title: 'Lista de Trámites',
    description: 'Ver todos los trámites',
    path: '/tasks',
    icon: <FileText className="h-4 w-4 text-amber-500" />,
    keywords: ['tramites', 'tareas', 'lista', 'tasks', 'cobranza'],
    shortcut: '⌘T',
  },
  {
    id: 'nav-billing-cuts',
    title: 'Cortes de Facturación',
    description: 'Ver cortes de comisión',
    path: '/billing-cuts',
    icon: <Receipt className="h-4 w-4 text-purple-500" />,
    keywords: ['cortes', 'facturacion', 'comisiones', 'billing', 'facturas'],
    shortcut: '⌘B',
  },
  {
    id: 'nav-billing-cuts-new',
    title: 'Generar Corte',
    description: 'Crear nuevo corte de facturación',
    path: '/billing-cuts/generate',
    icon: <PlusCircle className="h-4 w-4 text-purple-500" />,
    keywords: ['generar', 'corte', 'nuevo', 'crear', 'facturacion'],
  },
];

function matchesQuery(item: NavItem, query: string): boolean {
  const lowerQuery = query.toLowerCase();

  // Match against title
  if (item.title.toLowerCase().includes(lowerQuery)) return true;

  // Match against description
  if (item.description.toLowerCase().includes(lowerQuery)) return true;

  // Match against keywords
  if (item.keywords.some((kw) => kw.toLowerCase().includes(lowerQuery))) return true;

  return false;
}

function createNavResult(item: NavItem): SearchResult<NavItem> {
  return {
    id: item.id,
    title: item.title,
    subtitle: item.description,
    icon: item.icon,
    keywords: item.keywords,
    data: item,
    providerId: 'navigation',
  };
}

export const NavigationProvider: SearchProvider<NavItem> = {
  id: 'navigation',
  name: 'Navegación',
  icon: <Settings className="h-4 w-4" />,
  priority: 3, // Lower priority than entities
  placeholder: 'Ir a...',

  async search(query: string): Promise<SearchResult<NavItem>[]> {
    if (!query || query.length < 1) {
      return [];
    }

    const matches = NAVIGATION_ITEMS.filter((item) => matchesQuery(item, query));
    return matches.map(createNavResult);
  },

  getActions(item: NavItem): SearchAction[] {
    return [
      {
        id: 'navigate',
        label: 'Ir a esta página',
        shortcut: item.shortcut || '↵',
        path: item.path,
      },
    ];
  },

  // Show all navigation items when query is empty
  async getDefaultResults(): Promise<SearchResult<NavItem>[]> {
    return NAVIGATION_ITEMS.map(createNavResult);
  },
};
